import { codeWords } from './api'

// Every act the product records on the evidence chain, in words, and the group
// the Audit trail files it under (observation 3.19 of 17 September 2026).
//
// # WHY ONE MAP
//
// The Audit trail showed the chain's codes ("campaign.approved") and the
// vendor record's activity panel kept a partial map of its own, so the same
// act read two ways and most acts read as a code. This is the one place an
// act gets its words, and both screens read it.
//
// # WHY A TEST READS THIS FILE
//
// apps/tprm-api/internal/api/audit_labels_test.go reads every act the Go source
// can record and fails on one that has no line here, so a new act cannot reach
// an auditor as a dotted code. Keep one act per line in this shape, which is
// what that test reads:
//
//   'code': { label: 'Words', group: 'Group' },
//
// A label is a short sentence in the past tense, with no dots or underscores.
// The code stays visible in a row's detail on the Audit trail, where somebody
// matching the chain against another system needs it.

export const AUDIT_GROUPS = ['Vendors', 'Assessments', 'Findings', 'Risk', 'Offboarding', 'Settings', 'Connectors'] as const

export type AuditGroup = (typeof AUDIT_GROUPS)[number]

export const AUDIT_ACTIONS: Record<string, { label: string; group: AuditGroup }> = {
  // Vendors
  'vendor.created': { label: 'Vendor added', group: 'Vendors' },
  'vendor.edited': { label: 'Vendor record edited', group: 'Vendors' },
  'vendor.updated': { label: 'Vendor record updated', group: 'Vendors' },
  'vendor.approved': { label: 'Vendor approved', group: 'Vendors' },
  // The request step before due diligence (release 3.3).
  'vendor.request_approved': { label: 'Request approved', group: 'Vendors' },
  'vendor.request_rejected': { label: 'Request rejected', group: 'Vendors' },
  'vendor.approval_signatures_set_aside': { label: 'Signatures set aside by a new score', group: 'Vendors' },
  'vendor.suspended_due_diligence_sent': { label: 'Due diligence sent to a suspended vendor', group: 'Vendors' },
  'vendor.approval_signed': { label: 'Approval stage signed', group: 'Vendors' },
  'vendor.tier_changed': { label: 'Tier changed', group: 'Vendors' },
  'vendor.tier_override_requested': { label: 'Tier change asked for', group: 'Vendors' },
  'vendor.tier_override_approved': { label: 'Tier change approved', group: 'Vendors' },
  'vendor.tier_override_rejected': { label: 'Tier change rejected', group: 'Vendors' },
  'vendor.tier_override_withdrawn': { label: 'Tier change withdrawn', group: 'Vendors' },
  'vendor.contract_gap_raised': { label: 'Contract gap raised as a finding', group: 'Vendors' },
  'vendor.contract_gap_dismissed': { label: 'Contract gap dismissed', group: 'Vendors' },
  'vendor.tier_override_ended': { label: 'Tier set by hand ended', group: 'Vendors' },
  'vendor.tier_override_expired': { label: 'Tier set by hand reached its end date', group: 'Vendors' },
  'vendor.data_processing_recorded': { label: 'Data processing recorded', group: 'Vendors' },
  'vendor.domain_set': { label: 'Website recorded', group: 'Vendors' },
  'vendor.directory_domains_set': { label: 'Sign-in domains recorded', group: 'Vendors' },
  'vendor.onboarding_step_asserted': { label: 'Onboarding step done', group: 'Vendors' },
  'vendor.onboarding_step_withdrawn': { label: 'Onboarding step withdrawn', group: 'Vendors' },
  'vendor.subprocessor_recorded': { label: 'Sub-processor named', group: 'Vendors' },
  'vendor.subprocessor_updated': { label: 'Sub-processor updated', group: 'Vendors' },
  'vendor.subprocessor_withdrawn': { label: 'Sub-processor withdrawn', group: 'Vendors' },
  'vendor.reassessment_requested': { label: 'Reassessment requested', group: 'Vendors' },
  'vendor_contact.created': { label: 'Contact added', group: 'Vendors' },
  'vendor_contact.updated': { label: 'Contact updated', group: 'Vendors' },
  'vendor_contact.channel_changed': { label: 'Contact channel changed', group: 'Vendors' },
  'vendor_contact.deactivated': { label: 'Contact deactivated', group: 'Vendors' },
  'vendor_contact.erased': { label: 'Contact erased', group: 'Vendors' },
  'document.uploaded': { label: 'Document uploaded', group: 'Vendors' },
  'document.auto_renew_set': { label: 'Contract auto-renewal changed', group: 'Vendors' },
  'document.supplied': { label: 'Document supplied by the vendor', group: 'Vendors' },
  'document.extracted': { label: 'Clauses read from a document', group: 'Vendors' },
  'document.read': { label: 'Document read, personal data masked', group: 'Vendors' },
  'document.read_requested': { label: 'Document asked to be read again', group: 'Vendors' },
  'document.legacy_text_flagged': { label: 'Old kept text flagged as holding personal data', group: 'Vendors' },
  'document.terms_proposed': { label: 'Contract terms read from a document', group: 'Vendors' },
  'document.terms_decided': { label: 'Contract terms decided', group: 'Vendors' },
  'document.pi_findings_recorded': { label: 'Personal data values found in a document (masked)', group: 'Vendors' },
  'document.pi_finding_decided': { label: 'Personal data finding decided', group: 'Vendors' },
  'document.restricted': { label: 'Document restricted to evidence readers', group: 'Vendors' },
  'document.proposal_decided': { label: 'Document proposal decided', group: 'Vendors' },
  'document.accepted': { label: 'Document accepted', group: 'Vendors' },
  'document.declined': { label: 'Document declined', group: 'Vendors' },
  'document.legal_reviewed': { label: 'Legal review of an agreement recorded', group: 'Vendors' },
  'document.viewed': { label: 'Document opened', group: 'Vendors' },
  'evidence.viewed': { label: 'Answer evidence opened', group: 'Vendors' },
  'portfolio.exported': { label: 'Portfolio exported', group: 'Vendors' },
  'import.staged': { label: 'Import uploaded', group: 'Vendors' },
  'import.questions_mapped': { label: 'Imported questions mapped', group: 'Vendors' },
  'import.committed': { label: 'Import completed', group: 'Vendors' },
  'import.undone': { label: 'Import undone', group: 'Vendors' },
  'import.restored': { label: 'Undone import put back', group: 'Vendors' },
  'import.discarded': { label: 'Import in progress discarded', group: 'Vendors' },
  'import.mapping_saved': { label: 'Import mapping saved', group: 'Vendors' },
  'import.mapping_renamed': { label: 'Import mapping renamed', group: 'Vendors' },
  'import.mapping_copied': { label: 'Import mapping copied', group: 'Vendors' },
  'import.mapping_deleted': { label: 'Import mapping deleted', group: 'Vendors' },
  'vendor.retired': { label: 'Vendor retired by an undone import', group: 'Vendors' },
  'vendor.restored': { label: 'Retired vendor put back', group: 'Vendors' },

  // Assessments
  'campaign.drafted': { label: 'Campaign drafted', group: 'Assessments' },
  'campaign.approved': { label: 'Campaign approved', group: 'Assessments' },
  'campaign.held_for_second_person': { label: 'Invitation held for a second person', group: 'Assessments' },
  'campaign.cancelled': { label: 'Campaign cancelled', group: 'Assessments' },
  // A bulk send of several baselines, one campaign each (release 3.4, M7).
  'campaign_batch.drafted': { label: 'Bulk send drafted', group: 'Assessments' },
  'campaign_batch.approved': { label: 'Bulk send approved', group: 'Assessments' },
  'assessment.issued': { label: 'Questionnaire sent', group: 'Assessments' },
  'assessment.invitation_resent': { label: 'Invitation resent', group: 'Assessments' },
  'assessment.submitted': { label: 'Answers submitted', group: 'Assessments' },
  'assessment.imported': { label: 'Assessment imported', group: 'Assessments' },
  'assessment.scored': { label: 'Assessment scored', group: 'Assessments' },
  'assessment.band_changed': { label: 'Band changed', group: 'Assessments' },
  'assessment.band_overridden': { label: 'Band overridden', group: 'Assessments' },
  'assessment.override_lapsed': { label: 'Band override lapsed', group: 'Assessments' },
  'assessment.provisional_created': { label: 'Provisional assessment created', group: 'Assessments' },
  'assessment.provisional_confirmed': { label: 'Provisional assessment confirmed', group: 'Assessments' },
  'assessment.prefilled_confirmed': { label: 'Vendor confirmed the answers filled in for them', group: 'Assessments' },
  'assessment.derived_answer_corrected': { label: 'Vendor changed an answer read from the contract or record', group: 'Assessments' },
  'assessment.clarification_raised': { label: 'Question asked about an answer', group: 'Assessments' },
  'assessment.clarification_answered': { label: 'Question answered', group: 'Assessments' },
  'assessment.clarification_accepted': { label: 'Answer accepted', group: 'Assessments' },
  'assessment.clarification_rejected': { label: 'Answer not accepted', group: 'Assessments' },
  'assessment.clarification_withdrawn': { label: 'Question withdrawn', group: 'Assessments' },
  'response_evidence.attached': { label: 'Evidence attached', group: 'Assessments' },
  'response_evidence.removed': { label: 'Evidence removed', group: 'Assessments' },
  'questionnaire_version.published': { label: 'Questionnaire published', group: 'Assessments' },
  'questionnaire.archived': { label: 'Questionnaire archived', group: 'Assessments' },
  'questionnaire.restored': { label: 'Questionnaire restored', group: 'Assessments' },
  'reassessment_policy.updated': { label: 'Reassessment schedule changed', group: 'Assessments' },
  'trigger.raised': { label: 'Reassessment trigger raised', group: 'Assessments' },
  'trigger.reminder': { label: 'Certificate expiry reminder', group: 'Assessments' },
  'trigger.dismissed': { label: 'Reassessment trigger dismissed', group: 'Assessments' },
  'evidence.exported': { label: 'Evidence pack exported', group: 'Assessments' },

  // Findings
  'finding.raised': { label: 'Finding raised', group: 'Findings' },
  'finding.updated': { label: 'Finding updated', group: 'Findings' },
  'finding.started': { label: 'Finding work started', group: 'Findings' },
  'finding.resolved': { label: 'Finding resolved', group: 'Findings' },
  'finding.accepted': { label: 'Finding accepted as a risk', group: 'Findings' },
  'finding.withdrawn': { label: 'Finding withdrawn', group: 'Findings' },
  'finding.reopened': { label: 'Finding reopened', group: 'Findings' },
  'finding.moved': { label: 'Finding moved', group: 'Findings' },
  'finding.exception_approved': { label: 'Finding covered by an accepted risk', group: 'Findings' },
  'finding.register_decision': { label: 'Risk register decided a finding', group: 'Findings' },

  // Risk
  'exception.requested': { label: 'Risk acceptance requested', group: 'Risk' },
  'exception.stage_signed': { label: 'Risk acceptance stage signed', group: 'Risk' },
  'exception.granted': { label: 'Risk accepted', group: 'Risk' },
  'exception.rejected': { label: 'Risk acceptance refused', group: 'Risk' },
  'exception.remediation_required': { label: 'Risk acceptance sent back for work', group: 'Risk' },
  'exception.expired': { label: 'Risk acceptance expired', group: 'Risk' },
  'exception.clarified': { label: 'Exception clarified with the vendor', group: 'Risk' },
  'exception.routed': { label: 'Exception sent for review', group: 'Risk' },
  'exception.reviewed': { label: 'Exception reviewed', group: 'Risk' },
  'exception.escalated': { label: 'Exception escalated', group: 'Risk' },
  'exception.closed': { label: 'Exception closed on evidence', group: 'Risk' },
  'exception.closure_overdue': { label: 'Exception closure overdue', group: 'Risk' },
  'exception.acceptance_ending': { label: 'Risk acceptance or contract waiver ending soon', group: 'Risk' },
  'due_diligence.parameters_changed': { label: 'Exception parameters changed', group: 'Settings' },
  'exception_authority_version.drafted': { label: 'Who can accept risks: drafted', group: 'Settings' },
  'exception_authority_version.edited': { label: 'Who can accept risks: draft edited', group: 'Settings' },
  'exception_authority_version.promoted': { label: 'Who can accept risks: put in force', group: 'Settings' },
  'risk_model.created': { label: 'Scoring model created', group: 'Risk' },
  'risk_model_version.drafted': { label: 'Scoring model drafted', group: 'Risk' },
  'risk_model_version.edited': { label: 'Scoring model edited', group: 'Risk' },
  'risk_model_version.promoted': { label: 'Scoring model put in use', group: 'Risk' },
  'vendor.incident_recorded': { label: 'Incident recorded', group: 'Risk' },
  'vendor.incident_updated': { label: 'Incident updated', group: 'Risk' },
  'vendor.incident_contained': { label: 'Incident contained', group: 'Risk' },
  'vendor.incident_closed': { label: 'Incident closed', group: 'Risk' },
  'vendor.incident_reopened': { label: 'Incident reopened', group: 'Risk' },
  'vendor.incident_window_unaddressed': { label: 'Incident deadline missed', group: 'Risk' },
  'incident.windows_elapsed': { label: 'Incident deadlines passed', group: 'Risk' },
  'incident.duty_told': { label: 'Recorded that a regulator or the people affected were told', group: 'Risk' },
  'incident.breach_status': { label: 'Breach tool said what became of a vendor incident', group: 'Risk' },

  // Offboarding (glossary 7 Oct; "Exit plan" is the regulatory name and stays)
  'vendor.exit_proposed': { label: 'Offboarding proposed', group: 'Offboarding' },
  'vendor.exit_proposal_dismissed': { label: 'Offboarding proposal dismissed', group: 'Offboarding' },
  'vendor.exit_plan_recorded': { label: 'Exit plan recorded', group: 'Offboarding' },
  'vendor.exit_stage_signed': { label: 'Offboarding stage signed', group: 'Offboarding' },
  'vendor.exit_stage_declined': { label: 'Offboarding stage declined', group: 'Offboarding' },
  'vendor.offboarding_item_completed': { label: 'Offboarding evidence recorded', group: 'Offboarding' },
  'vendor.offboarding_item_reopened': { label: 'Offboarding evidence reopened', group: 'Offboarding' },
  'vendor.offboarded': { label: 'Vendor offboarded', group: 'Offboarding' },
  'vendor.legal_hold_placed': { label: 'Legal hold placed', group: 'Offboarding' },
  'vendor.legal_hold_released': { label: 'Legal hold released', group: 'Offboarding' },
  'offboarding.access_observed': { label: 'Vendor access checked', group: 'Offboarding' },

  // Settings
  'approval_matrix_version.drafted': { label: 'Approval rules drafted', group: 'Settings' },
  'approval_matrix_version.edited': { label: 'Approval rules edited', group: 'Settings' },
  'approval_matrix_version.proposed': { label: 'Approval rules proposed for activation', group: 'Settings' },
  'approval_matrix_version.promoted': { label: 'Approval rules put in use', group: 'Settings' },
  'onboarding_profile_version.drafted': { label: 'Onboarding steps drafted', group: 'Settings' },
  'onboarding_profile_version.edited': { label: 'Onboarding steps edited', group: 'Settings' },
  'onboarding_profile_version.promoted': { label: 'Onboarding steps put in use', group: 'Settings' },
  'custom_field.defined': { label: 'Custom field defined', group: 'Settings' },
  'custom_field.erased': { label: 'Custom field erased', group: 'Settings' },
  'tier_profile.updated': { label: 'Tier default changed', group: 'Settings' },
  // Baselines by group, type and tier, and vendor groups (release 3.4, M7).
  'baseline_rules.saved': { label: 'Baseline rules saved', group: 'Settings' },
  'vendor_group.created': { label: 'Vendor group added', group: 'Settings' },
  'vendor_group.changed': { label: 'Vendor group renamed', group: 'Settings' },
  'vendor_group.retired': { label: 'Vendor group retired', group: 'Settings' },
  'vendor_group.members_added': { label: 'Vendors added to a group', group: 'Settings' },
  'vendor_group.members_removed': { label: 'Vendors taken out of a group', group: 'Settings' },
  'tier_issuing.changed': { label: 'Second person rule for a tier changed', group: 'Settings' },
  'finding_deadlines.changed': { label: 'Finding deadlines changed', group: 'Settings' },
  'user.invited': { label: 'Person invited', group: 'Settings' },
  'user.roles_changed': { label: 'Roles changed', group: 'Settings' },
  'user.persona_changed': { label: 'Job changed', group: 'Settings' },
  'user.disabled': { label: 'Person switched off', group: 'Settings' },
  'user.enabled': { label: 'Person switched on', group: 'Settings' },
  'user.phone_changed': { label: 'Work phone changed', group: 'Settings' },
  'user.signed_in': { label: 'Signed in', group: 'Settings' },
  'user.sign_in_refused': { label: 'Sign-in refused', group: 'Settings' },
  'user.mfa_enrolled': { label: 'Second factor set up', group: 'Settings' },
  'user.mfa_removed': { label: 'Second factor removed', group: 'Settings' },
  'user.mfa_recovery_codes_replaced': { label: 'Recovery codes replaced', group: 'Settings' },
  'settings.sign_in_security_changed': { label: 'Sign-in security changed', group: 'Settings' },
  'user.password_set': { label: 'TPRM password set', group: 'Settings' },
  'user.password_sign_in_changed': { label: 'Password sign-in allowed or stopped', group: 'Settings' },
  'identity.persona_mapping_added': { label: 'Directory group mapped', group: 'Settings' },
  'identity.persona_mapping_removed': { label: 'Directory group unmapped', group: 'Settings' },
  'identity.offboarding_proof_changed': { label: 'Offboarding proof setting changed', group: 'Settings' },
  'messaging.policy_changed': { label: 'Messaging rules changed', group: 'Settings' },
  'tenant.mailbox_set': { label: 'Mailbox set', group: 'Settings' },
  'tenant.created': { label: 'Workspace created', group: 'Settings' },
  'settings.source_changed': { label: 'Settings source changed', group: 'Settings' },
  'settings.inherited_changed': { label: 'Setting inherited from FOCTTA Core changed', group: 'Settings' },
  'settings.platform_paired': { label: 'Paired with the platform', group: 'Settings' },
  'settings.suite_links_changed': { label: 'Suite links changed', group: 'Settings' },
  'settings.vendor_requests_changed': { label: 'Second person for vendor requests changed', group: 'Settings' },
  'settings.platform_unpaired': { label: 'Unpaired from the platform', group: 'Settings' },
  'suite.tprm_enabled': { label: 'Product switched on', group: 'Settings' },
  'suite.tprm_disabled': { label: 'Product switched off', group: 'Settings' },
  'launch.session_started': { label: 'Signed in from the suite', group: 'Settings' },
  'launch.session_ended': { label: 'Suite session ended', group: 'Settings' },
  'launch.refused': { label: 'Suite sign-in refused', group: 'Settings' },
  'launch.signal_received': { label: 'Suite signal received', group: 'Settings' },

  // Connectors
  'connector.created': { label: 'Connection added', group: 'Connectors' },
  'connector.updated': { label: 'Connection updated', group: 'Connectors' },
  'connector.removed': { label: 'Connection removed', group: 'Connectors' },
  'connector.inbound_key_issued': { label: 'Connection key issued', group: 'Connectors' },
  'connector.test_message_sent': { label: 'Test message sent', group: 'Connectors' },
  'procurement.request_received': { label: 'Purchase request received', group: 'Connectors' },
  'procurement.clearance_written': { label: 'Answer sent to the buying system', group: 'Connectors' },
  'procurement.clearance_not_written': { label: 'Answer not sent to the buying system', group: 'Connectors' },
  'procurement.clearance_changed': { label: 'Purchase answer changed', group: 'Connectors' },
  'procurement.approval_answered': { label: 'Buying system approval answered', group: 'Connectors' },
  'procurement.credential_issued': { label: 'Buying system key issued', group: 'Connectors' },
  'procurement.credential_revoked': { label: 'Buying system key revoked', group: 'Connectors' },
  'risk_register.created': { label: 'Risk register connected', group: 'Connectors' },
  'risk_register.updated': { label: 'Risk register connection updated', group: 'Connectors' },
  'risk_register.tested': { label: 'Risk register tested', group: 'Connectors' },
  'risk_register.paused': { label: 'Risk register paused', group: 'Connectors' },
  'risk_register.resumed': { label: 'Risk register resumed', group: 'Connectors' },
  'risk_register.treatment_given': { label: 'Risk register decides treatment', group: 'Connectors' },
  'risk_register.treatment_taken_back': { label: 'Treatment decided here again', group: 'Connectors' },
  // The vendor registry: the register other systems read and hear from.
  'registry.vendor_requested': { label: 'Vendor asked for from another system', group: 'Connectors' },
  'registry.suite_subscription_set': { label: 'FOCTTA Core set where vendor changes go', group: 'Connectors' },
  'registry.subscriber_added': { label: 'Vendor changes destination added', group: 'Connectors' },
  'registry.subscriber_changed': { label: 'Vendor changes destination changed', group: 'Connectors' },
  'registry.subscriber_tested': { label: 'Vendor changes destination tested', group: 'Connectors' },
  'registry.resync_requested': { label: 'Whole vendor register sent again', group: 'Connectors' },
  'registry.deliveries_retried': { label: 'Failed vendor changes sent again', group: 'Connectors' },
}

// The words for an act. One the map does not know yet, from a newer API than
// this console, still reads as words rather than a code.
export function auditLabel(code: string): string {
  return AUDIT_ACTIONS[code]?.label ?? (codeWords(code.replace(/[._]/g, ' ')) || code)
}

// The group an act is filed under, or Other for one the map does not know.
export function auditGroup(code: string): AuditGroup | 'Other' {
  return AUDIT_ACTIONS[code]?.group ?? 'Other'
}
